import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  /**
   * Initialize the application by checking token validity
   * This will be called during app initialization
   */
  async initializeApp(): Promise<void> {
    try {
      console.log('App initialization started');
      
      // Check if user is logged in
      if (this.authService.isLoggedIn()) {
        console.log('User has a token, checking expiration...');
        
        // Check if token is expired or will expire soon (within 5 minutes)
        if (this.authService.isTokenExpired(300000)) {
          console.log('Token expired or will expire soon, refreshing...');
          
          try {
            // Refresh token and wait for the result
            const newToken = await firstValueFrom(this.authService.refreshToken());
            
            if (newToken) {
              console.log('Token refreshed successfully during app initialization');
            } else {
              console.warn('Token refresh returned null during app initialization');
              this.handleSessionExpiration();
            }
          } catch (refreshError) {
            console.error('Error refreshing token during app initialization:', refreshError);
            this.handleSessionExpiration();
          }
        } else {
          console.log('Token is still valid');
        }
      } else {
        console.log('User is not logged in');
        this.cleanupExpiredTokens();
      }
    } catch (error) {
      console.error('Error during app initialization:', error);
      // Even if there is an error, continue app initialization
      // The user will be redirected to login if needed
      this.cleanupExpiredTokens();
    }
    
    return Promise.resolve();
  }
  
  /**
   * Clean up any expired or invalid tokens from localStorage
   */
  private cleanupExpiredTokens(): void {
    // Check if there are invalid tokens in localStorage
    const token = localStorage.getItem('access_token');
    
    if (token) {
      try {
        // If token is completely expired (not just about to expire)
        if (this.authService.isTokenExpired(0)) {
          console.log('Found expired token in localStorage, cleaning up');
          this.authService.clearAuthData();
        }
      } catch (e) {
        // If there was an error parsing the token, it's likely invalid
        console.log('Found invalid token in localStorage, cleaning up');
        this.authService.clearAuthData();
      }
    }
  }
  
  /**
   * Handle session expiration by clearing auth data
   */
  private handleSessionExpiration(): void {
    // Clear auth data but don't redirect if we're already on the login page
    this.authService.clearAuthData();
    
    const currentUrl = window.location.pathname;
    if (currentUrl !== '/login') {
      console.log('Session expired, redirecting to login page');
      // Add a query parameter to indicate session expiration
      this.router.navigate(['/login'], { queryParams: { expired: 'true' } });
    }
  }
} 